import { handleFetchUserDetails, updateFeedbackSheet } from "@/helpers/auth";
import { PINIA_PERSIST_OPTIONS } from "@/helpers/constants";
import { encryptToken } from "@/helpers/encryptionKeys";
import {
  setTrackedUser,
  setUserProperties,
  trackAmplitudeEvent,
} from "@/helpers/integrations/analytics/amplitude";
import {
  encryptKey,
  logoutAndClearCookies,
  requestLoginOTP,
  verifyEncryptedLoginToken,
} from "@/helpers/queries";
import { ISignIn } from "@/helpers/types/global";
import { defineStore } from "pinia";
import { useActivityStore } from "./activity.store";
import { useBusinessStore } from "./businesses.store";
import { useNumberStore } from "./numbers.store";
import { usePersonalizeStore } from "./personalization.store";
import { useSignUpStore } from "./signup.store";
import { useUsersStore } from "./users.store";
import { useUtilsStore } from "./utils.store";

import { handleError } from "@/api/handleError";
import { LoginResponse, magicLinkLogin } from "@/api/requests/auth";
import { getUnprovisionedPayment } from "@/api/requests/payments";
import { getSubscriptionStatus } from "@/api/requests/subscription";
import {
  getInvitations,
  getUserBusiness,
  getUserV2,
} from "@/api/requests/users";
import { useAuthRedirect } from "@/composables/useAuthRedirect";
import { useCustomerIO } from "@/composables/useCustomerIO";
import { resetPinia } from "@/helpers";
import { AMPLITUDE_EVENTS } from "@/helpers/integrations/analytics/events";
import { Profile } from "@/helpers/types/business";
import router from "@/router";
import { notify } from "@kyvg/vue3-notification";
import * as Sentry from "@sentry/vue";
import { useDialerV2Store } from "./dialerV2.store";
import { useTokenStore } from "./token.store";
import { EncryptKey } from "./types";
export interface AuthState {
  mobile?: string | null;
  otpSent: boolean;
  returnUrl?: string | null;
  email?: string | null;
  loading: boolean;
  authError: string;
  selectedPlanId?: number | string | null;
  couponCode?: string | null;
  scheme: string; // otp sender type
  encryptionData?: EncryptKey | null;
  isLoggingOut: boolean;
  userPropertySentToAmplitude?: {
    email?: string;
    phone?: string;
    first_name?: string;
    last_name?: string;
  } | null;
  resetPasswordData: {
    email: string;
    uid: string;
    token: string;
  };
  emailSignInErrMsg?: string | null;
  globalId?: string | null;
  defaultBusinessGlobalId?: string | null;
}

export const useAuthStore = defineStore("auth", {
  state: (): AuthState => ({
    // initialize state from local storage to enable user to stay logged in
    mobile: null,
    otpSent: false,
    email: null,
    returnUrl: null,
    loading: false,
    authError: "",
    scheme: "",
    encryptionData: null,
    couponCode: "",
    isLoggingOut: false,
    resetPasswordData: {
      email: "",
      uid: "",
      token: "",
    },
    userPropertySentToAmplitude: null,
    emailSignInErrMsg: "",
    globalId: "",
    defaultBusinessGlobalId: null,
  }),

  getters: {
    isAuthenticated(): boolean {
      const tokenStore = useTokenStore();

      return !!tokenStore.token;
    },
  },

  actions: {
    async getOTP(payload: ISignIn) {
      this.authError = "";
      this.loading = true;
      this.mobile = payload.mobile;

      const responseKeyData = (await encryptKey({ login_id: this.mobile })) as {
        data: EncryptKey;
      };

      this.encryptionData = responseKeyData.data;

      const response = (await requestLoginOTP(payload)) as {
        data: { email: string };
      };

      this.email = response.data.email;

      this.loading = false;
      return response; // we intentionally did not catch errors here
    },

    async login(
      payload: LoginResponse,
      isEmailLogin = false,
      _emailAddress?: string,
      isMagicLinkLogin = false,
      hasInvitesDelay = false,
      inviteBusinessId?: number
    ) {
      const businessStore = useBusinessStore();
      const userStore = useUsersStore();

      const utilsStore = useUtilsStore();
      const numberStore = useNumberStore();

      const personalization = usePersonalizeStore();
      const signupStore = useSignUpStore();

      const authRedirect = useAuthRedirect();

      const tokenStore = useTokenStore();

      try {
        this.loading = true;
        this.authError = "";

        let reqData: LoginResponse | undefined = undefined;

        if (!isEmailLogin && !isMagicLinkLogin) {
          if (!this.encryptionData && payload.mobile) {
            const responseKeyData = await encryptKey({
              login_id: payload.mobile,
            });

            this.encryptionData = responseKeyData.data;
          }

          if (!this.encryptionData) {
            throw new Error("Encryption data not found");
          }

          // Encrypt token
          const encryptedToken = encryptToken(
            this.encryptionData?.public_key,
            payload.token
          );

          const requestPayload = {
            // New encryption payload
            login_id: payload.mobile,
            auth_session_id: this.encryptionData.auth_session_id,
            otp: encryptedToken, // replaces the former token
          };

          const { data } = await verifyEncryptedLoginToken(requestPayload);

          reqData = data;
        } else if (isEmailLogin) {
          reqData = payload;
        } else {
          const token = payload.token;

          const magicLinkLoginResponse = await magicLinkLogin({ token });

          this.emailSignInErrMsg = "";

          reqData = magicLinkLoginResponse.data;
        }

        tokenStore.$patch({ token: reqData.token });

        const userResponse = await getUserV2();

        const currentUser = userResponse.data;

        this.mobile = currentUser.mobile;

        userStore.setCurrentUser({
          ...userStore.currentUserBusinessLevel,
          ...currentUser,
        });

        signupStore.globalId = currentUser?.global_id || "";

        const userBusinessResponse = await getUserBusiness({ pageSize: 10 });

        const userBusinesses = userBusinessResponse.data.data;

        businessStore.businesses = userBusinesses;

        userStore.profiles = userBusinesses;

        if (businessStore.businesses?.length) {
          let lastBusinessId: string | undefined = undefined;

          // get initial active business ID from local storage
          const lastBusinessIdFromLocalStorage =
            localStorage.getItem("lastBusinessId") || "";

          if (this.defaultBusinessGlobalId) {
            lastBusinessId = businessStore.businesses.find(
              (business) => business.global_id === this.defaultBusinessGlobalId
            )?.global_id;
          } else if (lastBusinessIdFromLocalStorage) {
            lastBusinessId = lastBusinessIdFromLocalStorage;
          }

          if (lastBusinessId) {
            // resumes to the last active business via this function
            await this.resumeLastBusiness(lastBusinessId);
          } else {
            // sets the first element in the array to the active number if no active number exists
            businessStore.activeBusinessMinimal = businessStore.businesses[0];

            await businessStore.fetchExtraBusinessDetails();
          }

          this.defaultBusinessGlobalId = null;
        }

        if (
          businessStore.activeBusinessMinimal?.id &&
          userStore.currentUserBusinessLevel?.id
        ) {
          // fetch the user profile in the current active business
          const activeBusinessId = businessStore.activeBusinessMinimal?.id;

          const [statusResponse] = await Promise.all([
            getSubscriptionStatus({ businessId: activeBusinessId }),
            numberStore.getUserPhonesFromBusiness(),
            businessStore.fetchActiveUserProfile(
              activeBusinessId,
              userStore.currentUserBusinessLevel?.id
            ),
          ]);

          numberStore.subscriptionStatus = statusResponse.data.data.status;
        }

        //if active business exists and is not verified, show verify NIN modal on login
        if (
          businessStore?.activeProfile?.business &&
          !businessStore?.activeProfile?.business?.verified &&
          businessStore.activeProfile?.profile?.role === "owner"
        ) {
          // only owners should be able to see the verification modal
          utilsStore.showVerifyNINModal = true;
        }

        const userEmail = userStore?.currentUserBusinessLevel?.email;

        if (userEmail) {
          setTrackedUser(userEmail); // tracks user using unique id i.e email
          setUserProperties({
            email: userEmail,
            phone: userStore.currentUserBusinessLevel?.mobile,
            first_name: userStore.currentUserBusinessLevel?.first_name,
            last_name: userStore.currentUserBusinessLevel?.last_name,
          });
        }

        void trackAmplitudeEvent(AMPLITUDE_EVENTS.LOGIN); // tracks login event

        if (utilsStore.signInSource) {
          void trackAmplitudeEvent(`User Signin - ${utilsStore.signInSource}`); // tracks the source of the login
        }

        Sentry.configureScope(function (scope) {
          scope.setUser({
            email: userStore?.currentUserBusinessLevel?.email ?? "",
          });
        });

        try {
          if (!userStore?.userProfile?.length) {
            // NO PENDING PAYMENT CHECK FROM SIGNIN ANYMORE. USERS THAT DON'T HAVE PROFILE SIMPLY HAVEN'T PAID
            // PROFILES ARE NOW CREATED IMMEDIATELY YOU PAY SINCE BUSINESS NUMBER IS ALSO BEING PROVIDED FROM PAYMENT
            this.email = userStore.currentUserBusinessLevel?.email || null;
            this.globalId = userStore.currentUserBusinessLevel?.global_id || "";

            const response = await getUnprovisionedPayment(); // check if there is a pending number

            // If this user was invited. i.e  they do not have a business and do not have pending payment.
            if (
              !businessStore.businesses?.length &&
              !response.data.data?.length
            ) {
              if (hasInvitesDelay) {
                await new Promise((resolve) => setTimeout(resolve, 6000)); // delay to allow invites to be fetched
              }

              const invitesResponse = await getInvitations({
                pageSize: 100,
                businessId: inviteBusinessId,
              });

              const invites = invitesResponse.data.data;

              const hasInvites = invites && invites?.length > 0;

              const hasAcceptedInvite = invites?.find(
                (invite) => invite.invitation_status === "accepted"
              );

              // If the user has pending invites, redirect to pending invites
              if (hasInvites && !hasAcceptedInvite) {
                localStorage.removeItem("lastBusinessId");

                await router.push("/pending-invites");
                return;
              }
            }

            if (response.data.data?.length) {
              utilsStore.setSetUpPayload(response.data.data);
              await router.push("/provision-number");
            } else {
              await authRedirect.handleCurrentSignupStage();
            }
          } else {
            // assign the user_role for the user to the role of the first receiver
            if (businessStore.activeProfile) {
              userStore.user_role = businessStore.activeProfile?.profile.role;
            }

            if (userStore.canAccessManagerView) {
              await handleFetchUserDetails();

              // sets feedback to true for new users. allow only existing users see modal
              if (
                personalization.userOnboardingStatus?.has_provisioned_number ===
                false
              ) {
                // false is for new user;
                await updateFeedbackSheet();
              }

              if (businessStore.goGlobalFirstSignIn) {
                await router.push("/managers/engagement");
                return;
              } else {
                await router.push("/managers/connected-devices");
              }
              if (this.returnUrl && this.returnUrl !== "/") {
                //  redirects to prev page before authentication
                await router.push(this.returnUrl);
              } else {
                await router.push("/managers/connected-devices");
              }
            } else {
              if (this.returnUrl && this.returnUrl !== "/") {
                await router.push(this.returnUrl);
              } else {
                await router.push("/");
              }
            }
          }
        } catch (error) {
          handleError({
            error,
            fallbackMessage: "Error occurred during Authentication. Try again",
          });
        }
      } catch (error) {
        const errorMsg = "Something went wrong, please try again";

        const handledError = handleError({ error, silent: true });

        if (handledError?.message?.includes("expired or is invalid")) {
          this.emailSignInErrMsg = handledError?.message;
          notify({
            type: "error",
            text:
              handledError?.message ||
              "The link you clicked has expired or is invalid",
          });
        } else {
          notify({
            type: "error",
            text:
              handledError?.message || "Something went wrong, please try again",
          });
        }

        this.authError = handledError?.message || errorMsg;

        localStorage.removeItem("lastBusinessId");
      } finally {
        this.loading = false;
      }

      this.encryptionData = null;
    },
    async resumeLastBusiness(lastBusinessId: string) {
      const businessStore = useBusinessStore();

      const businessId = Number(lastBusinessId);

      // finds the previous active business based on business id
      const previousActiveBusiness = businessStore.businesses?.find(
        (businessDetail: Profile) => {
          return businessDetail?.id === businessId;
        }
      );

      // sets active business
      if (previousActiveBusiness) {
        businessStore.activeBusinessMinimal = previousActiveBusiness;
      } else if (businessStore?.businesses) {
        businessStore.activeBusinessMinimal = businessStore.businesses[0];
      }

      if (businessStore.activeBusinessMinimal) {
        await businessStore.fetchExtraBusinessDetails();
      }
    },
    clearStores() {
      //clear stores
      const userStore = useUsersStore();
      const authStore = useAuthStore();
      const utilsStore = useUtilsStore();
      const numbersStore = useNumberStore();
      const businessStore = useBusinessStore();
      const tokenStore = useTokenStore();
      const dialerV2Store = useDialerV2Store();

      try {
        userStore.$reset();
        authStore.$reset();
        utilsStore.$reset();
        numbersStore.$reset();
        businessStore.$reset();
        tokenStore.$reset();
        dialerV2Store.$reset();

        const resetStores = resetPinia();

        resetStores.all();
      } catch (error) {
        handleError({
          error,
          silent: true,
        });
      }
    },
    async CLEAR_X_REFRESH_TOKEN(token: string) {
      try {
        await logoutAndClearCookies(token);
      } catch (error) {
        handleError({
          error,
          fallbackMessage: "refresh logout token error",
          silent: true,
        });
      }
    },
    async logout(redirect = true) {
      try {
        const businessStore = useBusinessStore();
        const tokenStore = useTokenStore();
        const analytics = useCustomerIO();
        const activityStore = useActivityStore();

        let lastBusinessId: number | null | undefined = undefined;

        // checks if there is an active business and keeps track of the business id before logout
        if (businessStore.activeBusinessMinimal) {
          lastBusinessId = businessStore.activeBusinessMinimal.id;
        }

        this.isLoggingOut = true;

        tokenStore.$patch({ token: null });
        this.mobile = null;
        activityStore.centrifuge?.disconnect();
        this.clearStores();
        Sentry.setUser(null);

        void analytics.reset();

        localStorage.removeItem("token");
        localStorage.removeItem("users");
        localStorage.removeItem("auth");
        localStorage.removeItem("numbers");
        localStorage.removeItem("dialer");

        document.cookie.split(";").forEach(function (cookie) {
          document.cookie = cookie
            .replace(/^ +/, "")
            .replace(
              /=.*/,
              "=;expires=" + new Date().toUTCString() + ";path=/"
            );
        });

        // check if baseurl exists before clearing store
        const baseUrl = localStorage.getItem("baseUrl");

        localStorage.clear();

        // set it after clearing local storage
        if (baseUrl) {
          localStorage.setItem("baseUrl", baseUrl);
        }

        sessionStorage.clear(); // clear

        // sets the business id after the local storage has been cleared on logout
        if (lastBusinessId) {
          localStorage.setItem("lastBusinessId", lastBusinessId.toString());
        }
      } catch (error) {
        handleError({
          error,
          fallbackMessage: "Error occurred during logout",
        });
      } finally {
        if (redirect) {
          await router.push("/sign-in");
        }

        this.isLoggingOut = false;
      }
    },
  },
  persist: PINIA_PERSIST_OPTIONS,
});
